import type { BannerAppearance } from '../../../lib/api';

/**
 * The look a banner has when nothing was ever configured: the same values the
 * API's contract defaults to. Used until GET /banner answers, and as the form
 * for a banner that was never published.
 */
export const DEFAULT_APPEARANCE: BannerAppearance = {
  layout: { shape: 'card', position: 'bottom_left', rail: true, languageControl: true, holdPage: false },
  buttons: { acceptAll: true, rejectAll: true, customise: true, rejectStyle: 'reject_all', order: 'reject_first' },
  text: {},
  look: {
    brand: '#1C3D73', button: '#1C3D73', buttonText: '#FFFFFF', surface: '#FFFFFF', text: '#101828', border: '#D9DEE7',
    font: 'inherit', baseSize: 14, radius: 'soft', logoAssetId: null, poweredBy: true, customCss: '',
  },
  template: { base: 'card', html: '', css: '', name: '' },
  reopen: { floating: 'bottom_left', icon: 'text' },
};

/** Fill every field the API may leave out so the form never reads undefined. */
export function withDefaults(a: Partial<BannerAppearance> | null | undefined, base: BannerAppearance = DEFAULT_APPEARANCE): BannerAppearance {
  return {
    layout: { ...base.layout, ...(a?.layout ?? {}) },
    buttons: { ...base.buttons, ...(a?.buttons ?? {}) },
    text: { ...(a?.text ?? {}) },
    look: { ...base.look, ...(a?.look ?? {}) },
    template: { ...base.template, ...(a?.template ?? {}) },
    reopen: { ...base.reopen, ...(a?.reopen ?? {}) },
  };
}

/** Deep equality on the appearance shape (plain JSON, no dates). */
export function sameAppearance(a: BannerAppearance, b: BannerAppearance): boolean {
  const canon = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(canon);
    if (v && typeof v === 'object') {
      return Object.fromEntries(Object.keys(v as Record<string, unknown>).sort().map((k) => [k, canon((v as Record<string, unknown>)[k])]));
    }
    return v;
  };
  return JSON.stringify(canon(a)) === JSON.stringify(canon(b));
}

/** The rules the API enforces, checked here too so Save is not offered for a draft the API refuses. */
export function appearanceIssues(a: BannerAppearance): string[] {
  const issues: string[] = [];
  if (a.buttons.acceptAll && !a.buttons.rejectAll) issues.push('Reject all stays while Accept all is shown: refusal must be as easy as acceptance.');
  if (!a.buttons.acceptAll && !a.buttons.rejectAll && !a.buttons.customise) issues.push('At least one button is needed.');
  if (a.layout.holdPage && a.layout.shape !== 'wall' && a.template?.base !== 'wall') issues.push('Holding the page needs the wall shape or the Modal wall design.');
  if (a.template.base === 'custom' && !a.template.html.trim()) issues.push('A custom template needs HTML.');
  for (const k of ['brand', 'button', 'buttonText', 'surface', 'text', 'border'] as const) {
    if (!/^#[0-9a-fA-F]{6}$/.test(a.look[k])) issues.push(`${LOOK_LABEL[k]}: colour as #RRGGBB.`);
  }
  return issues;
}

/** The built-in designs in gallery order, with the name and line the tiles show. */
export const DESIGNS: Array<{ key: Exclude<BannerAppearance['template']['base'], 'custom'>; name: string; description: string }> = [
  { key: 'card', name: 'Classic card', description: 'The standard panel with the side rail.' },
  { key: 'strip', name: 'Strip', description: 'One line along the bottom, text left, buttons right.' },
  { key: 'wall', name: 'Modal wall', description: 'Centred dialog with the page dimmed, categories inline.' },
  { key: 'sheet', name: 'Side sheet', description: 'Slides in from the side with the categories as a list.' },
  { key: 'toast', name: 'Corner toast', description: 'Small notice in a corner; Customise opens the sheet.' },
  { key: 'brand', name: 'Brand header', description: 'Logo band on top, body below, full-width buttons.' },
];

export const DESIGN_NAME: Record<BannerAppearance['template']['base'], string> = {
  ...Object.fromEntries(DESIGNS.map((d) => [d.key, d.name])) as Record<Exclude<BannerAppearance['template']['base'], 'custom'>, string>,
  custom: 'Custom design',
};

/** A built-in design whose html or css was changed, or anything custom. */
export function templateEdited(t: BannerAppearance['template']): boolean {
  return t.base === 'custom' || t.html.trim() !== '' || t.css.trim() !== '';
}

/**
 * What to call the design in a facts line or on the Design card:
 * "Side sheet", "Side sheet, edited", or the name the author gave it.
 */
export function designLabel(t: BannerAppearance['template']): string {
  if (t.name.trim()) return t.name.trim();
  if (t.base === 'custom') return DESIGN_NAME.custom;
  return templateEdited(t) ? `${DESIGN_NAME[t.base]}, edited` : DESIGN_NAME[t.base];
}

export const SHAPE_LABEL: Record<BannerAppearance['layout']['shape'], string> = { card: 'Card', bar: 'Bar', wall: 'Wall' };
export const POSITION_LABEL: Record<BannerAppearance['layout']['position'], string> = {
  bottom_left: 'Bottom left', bottom_right: 'Bottom right', bottom: 'Bottom, full width', top: 'Top',
};
export const REJECT_STYLE_LABEL: Record<BannerAppearance['buttons']['rejectStyle'], string> = {
  reject_all: 'Reject all', essential_only: 'Accept essential cookies only',
};
export const ORDER_LABEL: Record<BannerAppearance['buttons']['order'], string> = { reject_first: 'Reject first', accept_first: 'Accept first' };
export const FONT_LABEL: Record<BannerAppearance['look']['font'], string> = {
  inherit: 'Page font', system: 'System', manrope: 'Manrope', plex: 'IBM Plex Sans',
};
export const RADIUS_LABEL: Record<BannerAppearance['look']['radius'], string> = { square: 'Square', soft: 'Soft', round: 'Round' };
export const FLOATING_LABEL: Record<BannerAppearance['reopen']['floating'], string> = {
  off: 'Off', bottom_left: 'Bottom left', bottom_right: 'Bottom right',
};
export const ICON_LABEL: Record<BannerAppearance['reopen']['icon'], string> = { shield: 'Shield', cookie: 'Cookie', text: 'Text' };
export const LOOK_LABEL = {
  brand: 'Brand colour', button: 'Button colour', buttonText: 'Button text colour', surface: 'Surface colour', text: 'Text colour', border: 'Border colour',
} as const;

/** Length limits from the contract. */
export const TEXT_LIMITS = { title: 80, body: 400, accept: 40, reject: 40, customise: 40, noticeLink: 60, reopen: 40 } as const;

/** Every card edits through one setter so the preview sees each change. */
export type Update = (fn: (a: BannerAppearance) => BannerAppearance) => void;

export interface CardProps { a: BannerAppearance; update: Update; readOnly: boolean }
